"use client"

import { useEffect, useState } from "react"
import { ArrowUp, Home } from "lucide-react"
import "../assets/css/ScotchBonnetLearnMore.css"

export default function ScotchBonnetLearnMore() {
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      setShowBackToTop(scrollTop > 300)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  const goToHome = () => {
    window.location.href = "/"
  }

  return (
    <div className="learn-more-container">
      <h1>Learn More About Scotch Bonnet</h1>
      <p>
        The Scotch Bonnet is a vibrant and fiery chili pepper, instantly recognizable by its distinctive, bonnet-like
        shape and a color range that includes red, yellow, orange, and even chocolate brown when fully ripe. Native to
        the Caribbean and parts of Central and South America, it gets its name from its resemblance to the Scottish Tam
        o' Shanter hat. The pepper is botanically classified as <em>Capsicum chinense</em> and is closely related to the
        habanero.
      </p>
      <h2>Heat and Flavor Profile</h2>
      <p>
        Scotch Bonnet peppers are renowned for their intense heat, measuring between 100,000 and 350,000 Scoville Heat
        Units (SHU)—making them up to 70 times hotter than a jalapeño. Despite their fiery nature, they are also
        celebrated for their sweet, fruity, and floral flavor, with notes reminiscent of cherries, tomatoes, and apples.
        This unique balance of heat and sweetness makes them a staple in Caribbean cuisine, especially in jerk
        seasoning, hot sauces, stews, and marinades.
      </p>
      <h2>Culinary Uses</h2>
      <p>
        Highly versatile, Scotch Bonnets are used in a variety of dishes such as jerk chicken, pepper pot soup, curry
        goat, and escovitch fish. The peppers can be chopped fresh, blended into sauces, or even dried and powdered for
        seasoning. Their fruity undertones pair well with tropical fruits and add depth to both savory and sweet dishes.
      </p>
      <h2>Origins and History</h2>
      <p>
        The Scotch Bonnet traces its roots to the Amazon basin, where indigenous peoples like the Taino transported it
        throughout the Caribbean. Over time, it became deeply embedded in the region's food culture, particularly in
        Jamaica, which is now a leading exporter. Through the Columbian exchange, Scotch Bonnets and other chili
        varieties spread to Europe, Africa, and Asia, gaining popularity worldwide.
      </p>
      <h2>Handling and Storage</h2>
      <p>
        Due to their high capsaicin content, Scotch Bonnets should be handled with care—always wear gloves when
        preparing them to avoid skin irritation. To reduce their heat, you can remove the inner white membrane before
        adding to dishes. Fresh peppers are perishable and should be stored in the refrigerator, ideally wrapped in
        plastic, and used within two weeks.
      </p>
      <h2>Nutritional Value</h2>
      <p>
        Beyond their culinary appeal, Scotch Bonnets are rich in vitamins A, B, C, and K, as well as minerals like iron,
        copper, magnesium, and potassium. They are also high in antioxidant carotenoids, contributing to both flavor and
        health benefits.
      </p>
      <h2>Summary Table</h2>
      <table>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td data-label="Feature">Heat Level</td>
            <td data-label="Details">100,000–350,000 SHU</td>
          </tr>
          <tr>
            <td data-label="Feature">Flavor</td>
            <td data-label="Details">Fruity, sweet, floral, with cherry and apple notes</td>
          </tr>
          <tr>
            <td data-label="Feature">Color Varieties</td>
            <td data-label="Details">Red, yellow, orange, brown, green</td>
          </tr>
          <tr>
            <td data-label="Feature">Culinary Uses</td>
            <td data-label="Details">Jerk seasoning, sauces, stews, soups, marinades</td>
          </tr>
          <tr>
            <td data-label="Feature">Origin</td>
            <td data-label="Details">Caribbean, Central & South America (Amazon basin)</td>
          </tr>
          <tr>
            <td data-label="Feature">Nutritional</td>
            <td data-label="Details">Rich in vitamins A, B, C, K; minerals and antioxidants</td>
          </tr>
          <tr>
            <td data-label="Feature">Storage</td>
            <td data-label="Details">Refrigerate, use within 14–21 days</td>
          </tr>
          <tr>
            <td data-label="Feature">Handling Tips</td>
            <td data-label="Details">Wear gloves, avoid contact with eyes and skin</td>
          </tr>
        </tbody>
      </table>
      <p>
        Scotch Bonnet peppers are an essential ingredient for anyone seeking to add authentic Caribbean heat and flavor
        to their cooking.
      </p>

      {/* Navigation Arrows */}
      <div className="navigation-arrows">
        <button
          className={`nav-arrow back-to-top ${showBackToTop ? "visible" : ""}`}
          onClick={scrollToTop}
          data-tooltip="Back to Top"
          aria-label="Back to top"
        >
          <ArrowUp />
        </button>
        <button
          className="nav-arrow back-to-home"
          onClick={goToHome}
          data-tooltip="Back to Home"
          aria-label="Back to home"
        >
          <Home />
        </button>
      </div>
    </div>
  )
}
